import { useEffect, useRef, useState } from "react";

/**
 * Worker URLs must be absolute. Firebase Hosting rewrites every unmatched path
 * to index.html, so a relative "zbarWorker.js" resolved against a nested route
 * would silently load the HTML document as a script and the worker would never
 * come up.
 */
const publicPath = (file) => `${process.env.PUBLIC_URL || ""}/${file}`;

const DECODERS = [
  { file: "zbarWorker.js", alg: "zbar" },
  { file: "zxingWorker.js", alg: "zxing" },
];

/**
 * Spawns the zbar and zxing decoding workers once and routes their results to
 * the latest callback. Returns [zbarWorker, zxingWorker], null until ready.
 */
export function useQr(callback) {
  const [workers, setWorkers] = useState(null);

  // The workers are created once, so their message handlers must not close
  // over the callback of the first render.
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    let created = [];

    try {
      created = DECODERS.map(({ file, alg }) => {
        const worker = new Worker(publicPath(file));

        worker.onmessage = (event) => {
          if (!event.data) return;
          callbackRef.current({
            rawcode: event.data.data,
            type: event.data.type,
            milliseconds: event.data.ms,
            alg,
          });
        };

        worker.onerror = (event) => {
          console.error(
            `[qrcode-scan] decoder "${alg}" failed to start`,
            event.message || event
          );
        };

        return worker;
      });

      setWorkers(created);
    } catch (err) {
      // Blocked Worker construction (some hardened WebViews) must not take the
      // whole viewfinder down.
      console.error("[qrcode-scan] could not create decoding workers", err);
      created.forEach((worker) => worker.terminate());
      created = [];
    }

    return () => created.forEach((worker) => worker.terminate());
  }, []);

  return workers || [null, null];
}
